
import React, { useState } from 'react';

export const DemoFormSection = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    clinica: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'mensaje' && value.length > 500) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Guardar en el sistema de formularios de Readdy
      const formResponse = await fetch('https://readdy.ai/api/form/d625ohc3ij519rauvk3g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          nombre: formData.nombre,
          email: formData.email,
          telefono: formData.telefono,
          clinica: formData.clinica || 'No especificada',
          mensaje: formData.mensaje,
        }).toString(),
      });

      // Enviar correo electrónico
      const emailResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-demo-form-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            nombre: formData.nombre,
            email: formData.email,
            telefono: formData.telefono,
            clinica: formData.clinica || 'No especificada',
            mensaje: formData.mensaje,
          }),
        }
      );

      if (formResponse.ok) {
        setSubmitStatus('success');
        setFormData({ nombre: '', email: '', telefono: '', clinica: '', mensaje: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="demo-form" className="py-24 bg-[#0a0f1a]">
      <div className="max-w-3xl mx-auto px-6">
        <div className="bg-[#111827] border border-gray-800 rounded-3xl p-10 md:p-14">
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white italic mb-4">
              Agenda tu demo personalizada
            </h2>
            <p className="text-gray-400 text-lg">
              Descubre cómo Digital Line impulsa tu clínica en 20 minutos.
            </p>
          </div>

          {submitStatus === 'success' ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-r from-teal-400 to-pink-400 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-display font-semibold text-white mb-3">
                ¡Solicitud enviada!
              </h3>
              <p className="text-gray-400">
                Nos pondremos en contacto contigo pronto para agendar tu demo.
              </p>
            </div>
          ) : (
            <form 
              id="demo-reservation-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Laura García"
                    required
                    className="w-full bg-[#1a2234] border border-gray-700 rounded-xl px-5 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="laura@ejemplo.com"
                    required
                    className="w-full bg-[#1a2234] border border-gray-700 rounded-xl px-5 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="+34 600 000 000"
                    required
                    className="w-full bg-[#1a2234] border border-gray-700 rounded-xl px-5 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Clínica <span className="text-gray-600 normal-case">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    name="clinica"
                    value={formData.clinica}
                    onChange={handleChange}
                    placeholder="Clínica Estética..."
                    className="w-full bg-[#1a2234] border border-gray-700 rounded-xl px-5 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  ¿Cómo puede Mina ayudarte?
                </label>
                <textarea
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  placeholder="Dejar de perder llamadas, reactivar pacientes..."
                  rows={4}
                  maxLength={500}
                  className="w-full bg-[#1a2234] border border-gray-700 rounded-xl px-5 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 transition-colors resize-none"
                />
                <div className="text-right text-xs text-gray-600 mt-1">
                  {formData.mensaje.length}/500
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-5 rounded-2xl font-semibold text-white uppercase tracking-wider transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(90deg, #14b8a6 0%, #a855f7 50%, #ec4899 100%)',
                }}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Enviando...
                  </span>
                ) : (
                  'Reservar mi demo ahora'
                )}
              </button>

              {submitStatus === 'error' && (
                <p className="text-center text-red-400 text-sm">
                  Hubo un error al enviar. Por favor, inténtalo de nuevo.
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
